'use client';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Check, AlertCircle, Loader2, Star, Sun, MailCheck, Lightbulb, Wrench, Globe } from 'lucide-react';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { supabaseClient } from '../../lib/supabase';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';

// Zod Schemas
const signUpSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  agreeToTerms: z.boolean().refine(val => val === true, 'You must agree to the terms')
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword']
});

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
});

const resetPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

// Types
type AuthMode = 'signin' | 'signup' | 'reset' | 'email-sent' | 'loading';
type Toast = { id: string; type: 'success' | 'error' | 'info'; message: string };

// Toast Component
const Toast = ({ toast, onClose }: { toast: Toast; onClose: (id: string) => void }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, scale: 0.3 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
    className={`p-4 rounded-lg shadow-lg flex items-center gap-3 ${toast.type === 'success' ? 'bg-green-500 text-white' :
      toast.type === 'error' ? 'bg-red-500 text-white' :
        'bg-blue-500 text-white'
      }`}
  >
    {toast.type === 'success' && <Check size={20} />}
    {toast.type === 'error' && <AlertCircle size={20} />}
    {toast.type === 'info' && <Mail size={20} />}
    <span className="flex-1">{toast.message}</span>
    <button onClick={() => onClose(toast.id)} className="ml-2 hover:opacity-70">×</button>
  </motion.div>
);

// Loading Screen Component
const LoadingScreen = () => (
  <div className="fixed inset-0 bg-gradient-to-br from-orange-700 to-orange-700 flex items-center justify-center z-50">
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="text-center"
    >
      <motion.img
        src="/axist.png"
        className="w-16 h-16 mx-auto mb-4 rounded-[50%]"
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      />
      <div className="text-white text-lg font-medium">Welcome to Axist</div>
      <div className="text-blue-200 text-sm mt-2">Setting up your dashboard...</div>
    </motion.div>
  </div>
);

// Input Field Component
const InputField = ({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  icon: Icon,
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword
}: {
  label: string;
  type?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  icon: any;
  showPasswordToggle?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
}) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
      <input
        type={showPasswordToggle ? (showPassword ? 'text' : 'password') : type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
      />
      {showPasswordToggle && (
        <button
          type="button"
          onClick={onTogglePassword}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      )}
    </div>
    {error && (
      <motion.p
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-red-500 text-sm flex items-center gap-1"
      >
        <AlertCircle size={16} />
        {error}
      </motion.p>
    )}
  </div>
);

// Social Button Component
const SocialButton = ({ provider, onClick, loading }: { provider: 'google' | 'twitter'; onClick: () => void; loading: boolean }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    disabled={loading}
    className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
  >
    {loading ? (
      <Loader2 className="animate-spin" size={20} />
    ) : (
      <>
        {provider === 'google' ? (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
          </svg>
        )}
        Continue with {provider === 'google' ? 'Google' : 'Twitter'}
      </>
    )}
  </motion.button>
);

export default function AuthFlow() {
  const searchParams = useSearchParams();
  const initialModeParam = searchParams.get('mode');
  const [mode, setMode] = useState<AuthMode>(() => {
    if (initialModeParam === 'signup' || initialModeParam === 'signin' || initialModeParam === 'reset') {
      return initialModeParam;
    }
    return 'signin';
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [sentEmail, setSentEmail] = useState('');
  const [emailSentType, setEmailSentType] = useState<'signup' | 'reset'>('signup');

  // Form states
  const [signUpForm, setSignUpForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [signInForm, setSignInForm] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [resetForm, setResetForm] = useState({
    email: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { signUp, signIn, signInWithProvider, resetPassword, user, profile, loading: authLoading } = useAuth();
  const router = useRouter();

  const modeFromQuery = searchParams.get('mode');

  useEffect(() => {
    if (modeFromQuery === 'signup' || modeFromQuery === 'signin' || modeFromQuery === 'reset') {
      setMode(modeFromQuery);
    }
  }, [modeFromQuery]);

  // Redirect if already authenticated
  useEffect(() => {
    // Prefetch routes for faster transition
    router.prefetch('/dashboard');
    router.prefetch('/onboarding');

    if (user && !authLoading) {
      setMode('loading');
      // Use the profile from AuthProvider which is already loaded
      if (profile?.onboarding_completed) {
        router.push('/dashboard');
      } else {
        router.push('/onboarding');
      }
    }
  }, [user, authLoading, profile, router]);

  // Toast management
  const addToast = (type: Toast['type'], message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => removeToast(id), 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Validation helpers
  const validateSignUp = () => {
    try {
      signUpSchema.parse(signUpForm);
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.issues.forEach(error => {
          newErrors[String(error.path[0])] = error.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const validateSignIn = () => {
    try {
      signInSchema.parse(signInForm);
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.issues.forEach(error => {
          newErrors[String(error.path[0])] = error.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const validateReset = () => {
    try {
      resetPasswordSchema.parse(resetForm);
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        err.issues.forEach(error => {
          newErrors[String(error.path[0])] = error.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Real-time validation
  useEffect(() => {
    if (mode === 'signup' && Object.values(signUpForm).some(val => val)) {
      validateSignUp();
    }
  }, [signUpForm]);

  useEffect(() => {
    if (mode === 'signin' && (signInForm.email || signInForm.password)) {
      validateSignIn();
    }
  }, [signInForm]);

  useEffect(() => {
    if (mode === 'reset' && resetForm.email) {
      validateReset();
    }
  }, [resetForm]);

  // Form handlers
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSignUp()) return;

    setLoading(true);
    try {
      await signUp(signUpForm.email, signUpForm.password, signUpForm.name);
      setSentEmail(signUpForm.email);
      setEmailSentType('signup');
      setMode('email-sent');
      addToast('success', 'Account created! Please check your email to verify.');
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSignIn()) return;

    setLoading(true);
    try {
      await signIn(signInForm.email, signInForm.password);
      addToast('success', 'Welcome back!');
      setMode('loading');
      // The useEffect watching 'user' and 'profile' will handle redirect with router.push
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Sign in failed');
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateReset()) return;

    setLoading(true);
    try {
      await resetPassword(resetForm.email);
      setSentEmail(resetForm.email);
      setEmailSentType('reset');
      setMode('email-sent');
      addToast('success', 'Password reset email sent!');
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: 'google' | 'twitter') => {
    setSocialLoading(provider);
    try {
      await signInWithProvider(provider);
      // Supabase will redirect to the provider and then back to /auth/callback
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Social login failed');
      setSocialLoading(null);
    }
  };

  if (mode === 'loading') {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Side - Hero Content */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-r from-orange-900 via-orange-700 to-orange-500 text-white p-12 flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <img src="/axist1.png" alt="Axist" className="w-55 h-30" />
          </div>
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            Grow<br />
            <span className="text-blue-200">Your Business.</span>
          </h1>
          <p className="text-xl mb-8 text-blue-100 leading-relaxed">
            Axist helps brands and business market their brand and grow their business.
          </p>
          <div className="flex items-center gap-2 text-blue-200">
            <Star size={18} className="text-yellow-300" aria-hidden />
            <span>Trusted by 1,500+ creators, coaches, and marketers</span>
          </div>
          {/* Floating Dashboard Preview */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="mt-12 relative"
          >
            <div className="bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700">
              <div className="flex gap-2 mb-4">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-blue-500 rounded w-3/4"></div>
                <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                <div className="h-3 bg-gray-600 rounded w-5/6"></div>
                <div className="flex gap-2 mt-4">
                  <div className="h-8 bg-orange-500 rounded flex-1"></div>
                  <div className="h-8 bg-green-500 rounded flex-1"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Right Side - Auth Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {mode === 'signin' && (
              <motion.div
                key="signin"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y Zeke
                space-y-6"
              >
                <div className="text-center lg:text-left">
                  <div className="lg:hidden flex items-center justify-center mb-6">
                    <img src="/axist.png" alt="Axist" className="w-25 h-25 rounded-lg" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back!</h2>
                  <p className="text-gray-600">Sign in to your account to continue</p>
                </div>

                <form onSubmit={handleSignIn} className="space-y-4">
                  <InputField
                    label="Email"
                    type="email"
                    placeholder="Enter your email"
                    value={signInForm.email}
                    onChange={(value) => setSignInForm(prev => ({ ...prev, email: value }))}
                    error={errors.email}
                    icon={Mail}
                  />
                  <InputField
                    label="Password"
                    placeholder="Enter your password"
                    value={signInForm.password}
                    onChange={(value) => setSignInForm(prev => ({ ...prev, password: value }))}
                    error={errors.password}
                    icon={Lock}
                    showPasswordToggle
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={signInForm.rememberMe}
                        onChange={(e) => setSignInForm(prev => ({ ...prev, rememberMe: e.target.checked }))}
                        className="rounded border-gray-300 text-orange-700 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Remember me</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setMode('reset')}
                      className="text-sm text-orange-700 hover:text-orange-800 font-medium cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-700 text-white py-3 rounded-lg font-medium hover:bg-orange-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                    Sign In
                  </motion.button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">or</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <SocialButton
                    provider="google"
                    onClick={() => handleSocialSignIn('google')}
                    loading={socialLoading === 'google'}
                  />
                </div>

                <p className="text-center text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button
                    onClick={() => setMode('signup')}
                    className="text-orange-700 hover:text-orange-800 font-medium cursor-pointer"
                  >
                    Create Account
                  </button>
                </p>
              </motion.div>
            )}

            {mode === 'signup' && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="text-center lg:text-left">
                  <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
                    <img src="/axist.png" alt="Axist" className="w-25 h-25 rounded-lg" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center lg:justify-start gap-2">
                    <Sun size={28} className="text-orange-600" aria-hidden />
                    <span>Welcome to Axist!</span>
                  </h2>
                  <p className="text-gray-600">Create your account to start growing your business</p>
                </div>

                <form onSubmit={handleSignUp} className="space-y-4">
                  <InputField
                    label="Full Name"
                    placeholder="Enter your full name"
                    value={signUpForm.name}
                    onChange={(value) => setSignUpForm(prev => ({ ...prev, name: value }))}
                    error={errors.name}
                    icon={User}
                  />
                  <InputField
                    label="Email"
                    type="email"
                    placeholder="Enter your email"
                    value={signUpForm.email}
                    onChange={(value) => setSignUpForm(prev => ({ ...prev, email: value }))}
                    error={errors.email}
                    icon={Mail}
                  />
                  <InputField
                    label="Password"
                    placeholder="Create a password"
                    value={signUpForm.password}
                    onChange={(value) => setSignUpForm(prev => ({ ...prev, password: value }))}
                    error={errors.password}
                    icon={Lock}
                    showPasswordToggle
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                  />
                  <InputField
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    value={signUpForm.confirmPassword}
                    onChange={(value) => setSignUpForm(prev => ({ ...prev, confirmPassword: value }))}
                    error={errors.confirmPassword}
                    icon={Lock}
                    showPasswordToggle
                    showPassword={showConfirmPassword}
                    onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={signUpForm.agreeToTerms}
                      onChange={(e) => setSignUpForm(prev => ({ ...prev, agreeToTerms: e.target.checked }))}
                      className="mt-1 rounded border-gray-300 text-orange-700 focus:ring-blue-500"
                    />
                    <div className="text-sm text-gray-600">
                      I agree to the{' '}
                      <Link href="/terms" className="text-orange-700 hover:text-orange-800">Terms of Service</Link>
                      {' '}and{' '}
                      <Link href="/policy" className="text-orange-700 hover:text-orange-800">Privacy Policy</Link>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <AlertCircle size={16} />
                      {errors.agreeToTerms}
                    </p>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-700 text-white py-3 rounded-lg font-medium hover:bg-orange-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                    Sign Up
                  </motion.button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">or</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <SocialButton
                    provider="google"
                    onClick={() => handleSocialSignIn('google')}
                    loading={socialLoading === 'google'}
                  />
                </div>

                <p className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    onClick={() => setMode('signin')}
                    className="text-orange-700 hover:text-orange-900 font-medium cursor-pointer"
                  >
                    Sign In
                  </button>
                </p>
              </motion.div>
            )}

            {mode === 'reset' && (
              <motion.div
                key="reset"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="text-center lg:text-left">
                  <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
                    <img src="/axist.png" alt="Axist" className="w-8 h-8 rounded-lg" />
                    <span className="text-2xl font-bold text-gray-900">Axist</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
                  <p className="text-gray-600">Enter your email to receive a password reset link</p>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <InputField
                    label="Email"
                    type="email"
                    placeholder="Enter your email"
                    value={resetForm.email}
                    onChange={(value) => setResetForm(prev => ({ ...prev, email: value }))}
                    error={errors.email}
                    icon={Mail}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-700 text-white py-3 rounded-lg font-medium hover:bg-orange-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                    Send Reset Link
                  </motion.button>
                </form>

                <p className="text-center text-sm text-gray-600">
                  Remember your password?{' '}
                  <button
                    onClick={() => setMode('signin')}
                    className="text-orange-700 hover:text-orange-800 font-medium cursor-pointer"
                  >
                    Sign In
                  </button>
                </p>
              </motion.div>
            )}

            {mode === 'email-sent' && (
              <motion.div
                key="email-sent"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6 text-center"
              >
                <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
                  <img src="/axist.png" alt="Axist" className="w-8 h-8 rounded-lg" />
                  <span className="text-2xl font-bold text-gray-900">Axist</span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-orange-700" />
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MailCheck className="w-7 h-7 text-orange-700" aria-hidden />
                    <h2 className="text-3xl font-bold text-gray-900">Check Your Email!</h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    We've sent a {emailSentType === 'signup' ? 'verification' : 'password reset'} link to{' '}
                    <span className="font-medium text-gray-900">{sentEmail}</span>
                    <br />
                    Please click the link to {emailSentType === 'signup' ? 'activate your account' : 'reset your password'}.
                  </p>
                </div>

                <div className="space-y-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        if (emailSentType === 'signup') {
                          // Use Supabase's resend API for verification emails
                          const { error } = await supabaseClient.auth.resend({
                            type: 'signup',
                            email: sentEmail,
                            options: {
                              emailRedirectTo: `${window.location.origin}/auth/verify`,
                            },
                          });
                          if (error) throw error;
                        } else {
                          await resetPassword(sentEmail);
                        }
                        addToast('success', 'Email resent successfully!');
                      } catch (error) {
                        console.error('Resend email error:', error);
                        addToast('error', error instanceof Error ? error.message : 'Failed to resend email');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="w-full bg-orange-700 text-white py-3 rounded-lg font-medium hover:bg-orange-900 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                    Resend Email
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setMode(emailSentType === 'signup' ? 'signup' : 'reset');
                      if (emailSentType === 'signup') {
                        setSignUpForm(prev => ({ ...prev, email: sentEmail }));
                      } else {
                        setResetForm(prev => ({ ...prev, email: sentEmail }));
                      }
                    }}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Change Email Address
                  </motion.button>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-800 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" aria-hidden />
                    <span><span className="font-medium">Tip:</span> Check your spam folder if you don't see the email</span>
                  </p>
                </div>

                <p className="text-center text-sm text-gray-600">
                  {emailSentType === 'signup' ? 'Already verified?' : 'Remember your password?'}{' '}
                  <button
                    onClick={() => setMode('signin')}
                    className="text-orange-700 hover:text-orange-800 font-medium cursor-pointer"
                  >
                    Sign In
                  </button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast key={toast.id} toast={toast} onClose={removeToast} />
          ))}
        </AnimatePresence>
      </div>

      {/* Help Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 left-6 bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
      >
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Wrench className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline"><a href='/feedback'>Need help?</a></span>
        </div>
      </motion.button>

      {/* Language Selector */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 bg-white rounded-full px-4 py-2 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
      >
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Globe className="h-4 w-4" aria-hidden />
        </div>
      </motion.button>
    </div>
  );
}